// components/features/job-offers/JobOfferForm.tsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Save, Upload, X } from "lucide-react";
import { BaseJobOffer, JobOffer } from "../../../types/features/JobOffer";
import { kafintech_products } from "../../../constantes/kafintech-product";
import { useProduct } from "../../../hooks/core/useProduct";

interface JobOfferFormProps {
  initialData?: JobOffer;
  isEdit?: boolean;
  onSubmit: (data: BaseJobOffer, imageFile?: File) => Promise<void>;
  isLoading?: boolean;
}

export default function JobOfferForm({ initialData, isEdit = false, onSubmit, isLoading = false }: JobOfferFormProps) {
  const navigate = useNavigate();
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const {selectedProduct} = useProduct()
  const [formData, setFormData] = useState<BaseJobOffer>({
    title: "",
    title_en: "",
    description: "",
    description_en: "",
    location: "",
    location_en: "",
    cover_image: null,
    contract_type: "cdi",
    salary: null,
    deadline: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
    is_visible: true,
    product_name: selectedProduct || "all",
    product_name_en: selectedProduct || "all",
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title,
        title_en: initialData.title_en,
        description: initialData.description,
        description_en: initialData.description_en,
        location: initialData.location,
        location_en: initialData.location_en,
        cover_image: initialData.cover_image || null,
        contract_type: initialData.contract_type,
        salary: initialData.salary,
        deadline: initialData.deadline.slice(0, 16),
        is_visible: initialData.is_visible,
        product_name: initialData.product_name || "all",
        product_name_en: initialData.product_name_en || "all",
      });
    }
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Veuillez sélectionner une image");
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setFormData((prev) => ({ ...prev, cover_image: null }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(formData, imageFile || undefined);
  };

  const displayImage = imagePreview || formData.cover_image;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        {/* 📦 Produit concerné */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Produit concerné <span className="text-red-500">*</span>
          </label>
          <select
            name="product_name"
            value={formData.product_name || "all"}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
          >
            <option value="all">Tous les produits</option>
            {kafintech_products.map((product) => (
              <option key={product.value} value={product.value}>
                {product.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Sélectionnez "Tous les produits" si cette offre concerne tous les produits
          </p>
        </div>

        {/* Image de couverture */}
        <div className="mb-6">
          <div className="border-b border-gray-200 mb-4 pb-2">
            <h2 className="text-lg font-semibold text-gray-900">Image de couverture</h2>
          </div>

          {displayImage ? (
            <div className="relative inline-block">
              <img
                src={displayImage}
                alt="Cover"
                className="w-48 h-32 object-cover rounded-lg border border-gray-200"
              />
              <button
                type="button"
                onClick={removeImage}
                className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center w-48 h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary transition-colors">
              <div className="flex flex-col items-center justify-center">
                <Upload size={24} className="text-gray-400" />
                <p className="text-xs text-gray-500 mt-1">Image de couverture</p>
              </div>
              <input type="file" accept="image/*" onChange={handleImageSelect} className="hidden" />
            </label>
          )}
        </div>

        {/* Français */}
        <div className="mb-6">
          <div className="border-b border-gray-200 mb-4 pb-2">
            <h2 className="text-lg font-semibold text-gray-900">Français</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Titre (FR) *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50"
                placeholder="Développeur Full Stack"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lieu (FR) *</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50"
                placeholder="Abidjan, Côte d'Ivoire"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description (FR) *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50"
                placeholder="Description du poste, missions, prérequis..."
              />
            </div>
          </div>
        </div>

        {/* English */}
        <div className="mb-6">
          <div className="border-b border-gray-200 mb-4 pb-2">
            <h2 className="text-lg font-semibold text-gray-900">English</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title (EN) *</label>
              <input
                type="text"
                name="title_en"
                value={formData.title_en}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50"
                placeholder="Full Stack Developer"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location (EN) *</label>
              <input
                type="text"
                name="location_en"
                value={formData.location_en}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50"
                placeholder="Abidjan, Ivory Coast"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description (EN) *</label>
              <textarea
                name="description_en"
                value={formData.description_en}
                onChange={handleChange}
                required
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50"
                placeholder="Job description, missions, requirements..."
              />
            </div>
          </div>
        </div>

        {/* Informations complémentaires */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type de contrat *</label>
            <select
              name="contract_type"
              value={formData.contract_type}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50"
            >
              <option value="cdi">CDI</option>
              <option value="cdd">CDD</option>
              <option value="freelance">Freelance</option>
              <option value="internship">Stage</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Salaire</label>
            <input
              type="text"
              name="salary"
              value={formData.salary || ""}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50"
              placeholder="500 000 - 800 000 FCFA"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date limite *</label>
            <input
              type="datetime-local"
              name="deadline"
              value={formData.deadline}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50"
            />
          </div>

          <div className="flex items-center mt-7">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="is_visible"
                checked={formData.is_visible}
                onChange={handleCheckbox}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm font-medium text-gray-700">Publier cette offre sur le site</span>
            </label>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {isLoading ? "Enregistrement..." : isEdit ? "Mettre à jour" : "Publier"}
        </button>
      </div>
    </form>
  );
}